import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Briefcase, 
  TrendingUp, 
  Lock, 
  Unlock, 
  FileText, 
  CreditCard,
  CheckCircle,
  Clock,
  AlertCircle,
  Download,
  Upload,
  Cpu,
  Activity,
  ArrowRight
} from 'lucide-react';
import axios from 'axios';

// --- API Helper ---
const API_URL = process.env.REACT_APP_API_URL || 'https://api.daggpt.network';

const api = axios.create({
  baseURL: API_URL,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('daggpt_token') || localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// --- Components ---

const BentoCard = ({ children, className = '', delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay }}
    className={`bg-white/60 backdrop-blur-[40px] border border-slate-200/50 shadow-xl rounded-3xl p-6 ${className}`}
  >
    {children}
  </motion.div>
);

export default function InvestorDashboard() {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [showKyc, setShowKyc] = useState(false);
  const [actionNode, setActionNode] = useState(null);
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [amount, setAmount] = useState('');
  const [expandedDate, setExpandedDate] = useState(null);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/investor/dashboard');
      setDashboardData(res.data);
    } catch (err) {
      setError('Failed to load investor dashboard');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleWithdraw = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/investor/withdraw', { 
        nodeId: actionNode.id,
        amountCredits: Number(amount)
      });
      alert('Withdrawal requested successfully!');
      setShowWithdraw(false);
      setAmount('');
      fetchDashboard();
    } catch (err) {
      alert(err.response?.data?.error || 'Error requesting withdrawal');
    }
  };

  const handleTransfer = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/investor/transfer-to-ai', { 
        nodeId: actionNode.id,
        amountCredits: Number(amount)
      });
      alert('Credits successfully transferred to AI balance!');
      setShowTransfer(false);
      setAmount('');
      fetchDashboard();
    } catch (err) {
      alert(err.response?.data?.error || 'Error transferring credits');
    }
  };

  const handleKycSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    try {
      await api.post('/api/investor/kyc/submit', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      alert('KYC Application submitted successfully!');
      setShowKyc(false);
    } catch (err) {
      alert(err.response?.data?.error || 'Error submitting KYC');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full w-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full w-full text-slate-600">
        <AlertCircle size={48} className="mb-4 text-red-500" />
        <h2 className="text-xl font-bold">Error</h2>
        <p>{error}</p>
      </div>
    );
  }

  if (!dashboardData?.isInvestor && dashboardData?.nodes?.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full w-full text-slate-600">
        <Lock size={48} className="mb-4" />
        <h2 className="text-2xl font-bold mb-2 text-slate-900">Investor Access Restricted</h2>
        <p className="max-w-md text-center">You need to purchase a node to access the Investor Dashboard.</p>
      </div>
    );
  }

  const { activeBalance, nodes, withdrawals, history } = dashboardData;
  const totalDailyYield = nodes.reduce((sum, n) => sum + Number(n.daily_yield), 0);
  const totalInvestment = nodes.reduce((sum, n) => sum + Number(n.price), 0);

  return (
    <div className="w-full h-full p-8 overflow-y-auto custom-scrollbar text-slate-900 bg-slate-50/50">
      
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-slate-900 to-slate-600">
            Investor Dashboard
          </h1>
          <p className="text-slate-500 mt-1">Manage your nodes, yields, and withdrawals.</p>
        </div>
        <button 
          onClick={() => setShowKyc(true)}
          className="flex items-center gap-2 px-6 py-2.5 bg-white border border-slate-200 rounded-full shadow-sm hover:shadow-md transition-all font-medium text-slate-700"
        >
          <FileText size={18} />
          Complete KYC
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        {/* Total Investment */}
        <BentoCard delay={0.1}>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-3 bg-blue-50 text-blue-600 rounded-2xl">
              <Briefcase size={24} />
            </div>
            <h3 className="text-lg font-semibold text-slate-700">Total Investment</h3>
          </div>
          <div className="mt-4">
            <span className="text-4xl font-bold">${totalInvestment.toLocaleString()}</span>
            <span className="text-slate-500 ml-2">USD</span>
          </div>
        </BentoCard>

        {/* Daily Yield */}
        <BentoCard delay={0.2}>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-3 bg-purple-50 text-purple-600 rounded-2xl">
              <TrendingUp size={24} />
            </div>
            <h3 className="text-lg font-semibold text-slate-700">Total Daily Yield</h3>
          </div>
          <div className="mt-4">
            <span className="text-4xl font-bold">{totalDailyYield.toLocaleString()}</span>
            <span className="text-slate-500 ml-2">Credits / Day</span>
          </div>
        </BentoCard>

        {/* Active Balance */}
        <BentoCard delay={0.3}>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-3 bg-indigo-50 text-indigo-600 rounded-2xl">
              <Activity size={24} />
            </div>
            <h3 className="text-lg font-semibold text-slate-700">Active Balance</h3>
          </div>
          <div className="mt-4">
            <span className="text-4xl font-bold">{Math.floor(activeBalance).toLocaleString()}</span>
            <span className="text-slate-500 ml-2">Credits</span>
          </div>
        </BentoCard>
      </div>

      <div className="grid grid-cols-1 gap-6">
        
        {/* Full Column */}
        <div className="space-y-6">
          
          {/* My Nodes */}
          <BentoCard delay={0.4}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                <Cpu size={20} className="text-slate-500" />
                My GPU Nodes
              </h3>
            </div>
            
            <div className="space-y-4">
              {nodes.map(node => (
                <div key={node.id} className="p-5 rounded-2xl border border-slate-100 bg-slate-50 flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <h4 className="font-semibold text-slate-800 text-lg">{node.node_name}</h4>
                    <p className="text-sm text-slate-500">Purchased: {new Date(node.purchased_at).toLocaleDateString()} &bull; Route: <span className="capitalize">{node.payment_method || 'Fiat'}</span></p>
                    <div className="mt-2 text-sm">
                      Available Yield: <span className="font-bold text-slate-900">{Number(node.available_yield || 0).toLocaleString()}</span> credits
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <div className="text-right mb-2 md:mb-0">
                      <p className="font-bold text-purple-600">+{Number(node.daily_yield).toLocaleString()}</p>
                      <p className="text-xs text-slate-400">credits / day</p>
                    </div>
                    <div className="flex gap-2">
                      <button 
                        onClick={() => { setActionNode(node); setAmount(''); setShowTransfer(true); }}
                        className="px-4 py-2 bg-indigo-50 text-indigo-700 font-medium rounded-xl hover:bg-indigo-100 transition-colors text-sm flex items-center gap-2"
                      >
                        <Activity size={16} />
                        Transfer to AI
                      </button>
                      <button 
                        onClick={() => { setActionNode(node); setAmount(''); setShowWithdraw(true); }}
                        className="px-4 py-2 bg-slate-900 text-white font-medium rounded-xl hover:bg-slate-800 transition-colors text-sm flex items-center gap-2"
                      >
                        <CreditCard size={16} />
                        Withdraw
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {nodes.length === 0 && (
                <div className="text-center p-8 text-slate-500 border border-dashed border-slate-200 rounded-2xl">
                  No nodes purchased yet.
                </div>
              )}
            </div>
          </BentoCard>

          {/* Withdrawal History */}
          <BentoCard delay={0.5}>
            <h3 className="text-xl font-bold text-slate-800 mb-6 flex items-center gap-2">
              <Clock size={20} className="text-slate-500" />
              Withdrawal History
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-100 text-slate-500">
                    <th className="pb-3 font-medium">Date</th>
                    <th className="pb-3 font-medium">Amount (Credits)</th>
                    <th className="pb-3 font-medium">Fiat Value</th>
                    <th className="pb-3 font-medium">Method</th>
                    <th className="pb-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {withdrawals.map(w => (
                    <tr key={w.id}>
                      <td className="py-4">{new Date(w.created_at).toLocaleDateString()}</td>
                      <td className="py-4 font-semibold">{Number(w.amount_credits).toLocaleString()}</td>
                      <td className="py-4">${Number(w.fiat_value).toLocaleString()}</td>
                      <td className="py-4 capitalize">{w.method}</td>
                      <td className="py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          w.status === 'processed' ? 'bg-emerald-100 text-emerald-700' :
                          w.status === 'rejected' ? 'bg-red-100 text-red-700' :
                          'bg-amber-100 text-amber-700'
                        }`}>
                          {w.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {withdrawals.length === 0 && (
                    <tr>
                      <td colSpan="5" className="py-8 text-center text-slate-500">
                        No withdrawals yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </BentoCard>

          {/* Daily Yield History */}
          <BentoCard delay={0.55}>
            <h3 className="text-xl font-bold text-slate-800 mb-6 flex items-center gap-2">
              <TrendingUp size={20} className="text-slate-500" />
              Daily Yield History
            </h3>
            <div className="space-y-3">
              {Object.entries(
                (history || []).reduce((acc, h) => {
                  const d = new Date(h.created_at).toLocaleDateString();
                  if (!acc[d]) acc[d] = { total: 0, items: [] };
                  acc[d].total += Number(h.amount);
                  acc[d].items.push(h);
                  return acc;
                }, {})
              ).map(([date, data]) => (
                <div key={date} className="border border-slate-100 rounded-2xl overflow-hidden bg-white">
                  <div 
                    className="p-4 flex items-center justify-between cursor-pointer hover:bg-slate-50 transition-colors"
                    onClick={() => setExpandedDate(expandedDate === date ? null : date)}
                  >
                    <span className="font-medium text-slate-700">{date}</span>
                    <div className="flex items-center gap-4">
                      <span className="font-bold text-purple-600">+{data.total.toLocaleString()} credits</span>
                      <span className="text-slate-400">
                        {expandedDate === date ? '▼' : '▶'}
                      </span>
                    </div>
                  </div>
                  
                  {expandedDate === date && (
                    <div className="p-4 bg-slate-50 border-t border-slate-100 space-y-2">
                      {data.items.map((item, idx) => {
                        const node = nodes.find(n => n.id === item.node_id);
                        return (
                          <div key={idx} className="flex justify-between text-sm py-1 border-b border-slate-200/50 last:border-0">
                            <span className="text-slate-600">{node ? node.node_name : `Node #${item.node_id}`}</span>
                            <span className="text-slate-700 font-medium">+{Number(item.amount).toLocaleString()}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
              {(!history || history.length === 0) && (
                <div className="text-center p-8 text-slate-500 border border-dashed border-slate-200 rounded-2xl">
                  No yields generated yet.
                </div>
              )}
            </div>
          </BentoCard>
        </div>
      </div>

      {/* KYC Modal */}
      {showKyc && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto custom-scrollbar p-8"
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-slate-900">Identity Verification (KYC)</h2>
              <button onClick={() => setShowKyc(false)} className="text-slate-400 hover:text-slate-600">
                ✕
              </button>
            </div>
            
            <form onSubmit={handleKycSubmit} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1">First Name</label>
                  <input name="firstName" required className="w-full border border-slate-200 rounded-xl px-4 py-2.5 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none" />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1">Last Name</label>
                  <input name="lastName" required className="w-full border border-slate-200 rounded-xl px-4 py-2.5 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none" />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">Phone Number</label>
                <input name="phone" type="tel" required className="w-full border border-slate-200 rounded-xl px-4 py-2.5 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none" />
              </div>

              <div className="border-t border-slate-100 pt-6">
                <h3 className="font-semibold text-slate-800 mb-4">Bank Details</h3>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 block mb-1">Account Name</label>
                    <input name="bankAccountName" required className="w-full border border-slate-200 rounded-xl px-4 py-2.5 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 block mb-1">Bank Name</label>
                    <input name="bankName" required className="w-full border border-slate-200 rounded-xl px-4 py-2.5 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 block mb-1">Code Type</label>
                    <select name="bankCodeType" required className="w-full border border-slate-200 rounded-xl px-4 py-2.5 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none bg-white">
                      <option value="IFSC">IFSC Code</option>
                      <option value="SWIFT">SWIFT Code</option>
                      <option value="IBAN">IBAN</option>
                      <option value="ROUTING">Routing Number</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 block mb-1">Bank Code</label>
                    <input name="bankCode" required className="w-full border border-slate-200 rounded-xl px-4 py-2.5 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none" />
                  </div>
                </div>
              </div>

              <div className="border-t border-slate-100 pt-6">
                <h3 className="font-semibold text-slate-800 mb-4">Document Upload</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 block mb-1">ID Card (Front)</label>
                    <input type="file" name="idFront" accept="image/*" required className="w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 block mb-1">ID Card (Back)</label>
                    <input type="file" name="idBack" accept="image/*" required className="w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 block mb-1">Selfie Video (Max 50MB)</label>
                    <input type="file" name="video" accept="video/*" required className="w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
                  </div>
                </div>
              </div>

              <div className="pt-4 flex justify-end gap-3">
                <button type="button" onClick={() => setShowKyc(false)} className="px-6 py-2.5 rounded-xl font-medium text-slate-600 hover:bg-slate-100">
                  Cancel
                </button>
                <button type="submit" className="px-6 py-2.5 rounded-xl font-medium bg-slate-900 text-white hover:bg-slate-800 flex items-center gap-2">
                  <CheckCircle size={18} />
                  Submit Application
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Transfer to AI Modal */}
      {showTransfer && actionNode && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-8"
          >
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Transfer to AI Balance</h2>
            <p className="text-slate-500 text-sm mb-6">
              Move credits from <strong>{actionNode.node_name}</strong> to your active balance to spend on AI generation.
            </p>
            
            <form onSubmit={handleTransfer} className="space-y-4">
              <div>
                <label className="text-xs font-medium text-slate-500 mb-1 block">Amount (Credits)</label>
                <input 
                  type="number" 
                  required
                  min="1"
                  max={actionNode.available_yield || 0}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-slate-900 focus:outline-none focus:border-indigo-500 transition-colors"
                  placeholder="Enter amount..."
                />
                <p className="text-xs text-slate-400 mt-1">Available: {Number(actionNode.available_yield || 0).toLocaleString()}</p>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => setShowTransfer(false)} className="px-5 py-2.5 rounded-xl font-medium text-slate-600 hover:bg-slate-100">
                  Cancel
                </button>
                <button type="submit" className="px-5 py-2.5 rounded-xl font-medium bg-indigo-600 text-white hover:bg-indigo-700">
                  Transfer Credits
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Withdraw Modal */}
      {showWithdraw && actionNode && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-8"
          >
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Withdraw Yield</h2>
            <p className="text-slate-500 text-sm mb-6">
              Withdraw funds from <strong>{actionNode.node_name}</strong>. Payout will be processed via: 
              <strong className="block mt-1 text-slate-700 capitalize">{actionNode.payment_method === 'dgcc' ? 'Crypto (DGCC)' : actionNode.payment_method === 'fiat' ? 'Fiat (Bank Transfer)' : 'Crypto (BEP20/ERC20)'}</strong>
            </p>
            
            <form onSubmit={handleWithdraw} className="space-y-4">
              <div>
                <label className="text-xs font-medium text-slate-500 mb-1 block">Amount (Credits)</label>
                <input 
                  type="number" 
                  required
                  min="50000"
                  max={actionNode.available_yield || 0}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-slate-900 focus:outline-none focus:border-slate-500 transition-colors"
                  placeholder="Enter amount..."
                />
                <div className="flex justify-between mt-1">
                  <p className="text-xs text-slate-400">Available: {Number(actionNode.available_yield || 0).toLocaleString()}</p>
                  <p className="text-xs text-slate-500 font-medium">Min: 50,000</p>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => setShowWithdraw(false)} className="px-5 py-2.5 rounded-xl font-medium text-slate-600 hover:bg-slate-100">
                  Cancel
                </button>
                <button type="submit" className="px-5 py-2.5 rounded-xl font-medium bg-slate-900 text-white hover:bg-slate-800">
                  Request Withdraw
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
}
